import React, { useState, useEffect, useCallback, useRef } from "react";
import { Clock, Play, Square, Coffee, Loader2, Utensils } from "lucide-react";
import { toast } from "sonner";
import { timeclockApi } from "../lib/api";
import { getCurrentPositionOnce } from "../lib/gps";

/** Worked seconds for the CURRENT open session (live), minus breaks. */
export function sessionSeconds(entry, nowMs) {
  if (!entry) return 0;
  const ci = new Date(entry.clockIn).getTime();
  const end = entry.clockOut ? new Date(entry.clockOut).getTime() : nowMs;
  let total = (end - ci) / 1000;
  (entry.breaks || []).forEach((b) => {
    const bs = new Date(b.start).getTime();
    const be = b.end ? new Date(b.end).getTime() : nowMs;
    total -= Math.max(0, (be - bs) / 1000);
  });
  return Math.max(0, total);
}

export function fmtDuration(secs) {
  const s = Math.max(0, Math.round(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/** Shared time-clock state hook: polls /timeclock/me and exposes actions. */
export function useTimeClock(active = true) {
  const [state, setState] = useState(null); // { status, entry, todaySeconds, weekSeconds }
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await timeclockApi.me();
      setState(data);
    } catch {
      /* ignore (e.g. logged out) */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!active) return;
    refresh();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [active, refresh]);

  const act = useCallback(
    async (fn, okMsg) => {
      setBusy(true);
      try {
        const data = await fn();
        setState(data);
        if (okMsg) toast.success(okMsg);
        window.dispatchEvent(new Event("timeclock-changed"));
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Action failed");
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  return {
    state,
    busy,
    loading,
    refresh,
    clockIn: async () => {
      const gps = await getCurrentPositionOnce();
      return act(() => timeclockApi.clockIn(gps), "Clocked in");
    },
    clockOut: async () => {
      const gps = await getCurrentPositionOnce();
      return act(() => timeclockApi.clockOut(gps), "Clocked out");
    },
    lunchStart: () => act(timeclockApi.lunchStart, "Lunch started"),
    lunchEnd: () => act(timeclockApi.lunchEnd, "Back from lunch"),
  };
}

const STATUS_META = {
  clocked_out: { label: "Clocked out", dot: "bg-slate-400", text: "text-slate-500" },
  clocked_in: { label: "Clocked in", dot: "bg-emerald-500", text: "text-emerald-600" },
  on_lunch: { label: "On lunch", dot: "bg-amber-500", text: "text-amber-600" },
};

/**
 * Time-clock control panel. Drop into the user menu or a mobile sheet.
 * Renders status, a live session timer, and Clock In / Lunch / Clock Out buttons.
 */
export function TimeClockPanel({ clock, compact = false, stacked = false }) {
  const { state, busy, loading, clockIn, clockOut, lunchStart, lunchEnd } = clock;
  const status = state?.status || "clocked_out";
  const meta = STATUS_META[status] || STATUS_META.clocked_out;
  const [nowMs, setNowMs] = useState(Date.now());
  const tickRef = useRef(null);

  // Live-tick only while actively working (not on lunch / clocked out).
  useEffect(() => {
    if (status === "clocked_in") {
      tickRef.current = setInterval(() => setNowMs(Date.now()), 1000);
      return () => clearInterval(tickRef.current);
    }
    setNowMs(Date.now());
  }, [status, state]);

  const live = state?.entry ? sessionSeconds(state.entry, nowMs) : 0;

  return (
    <div data-testid="time-clock-panel" className={compact ? "" : "px-3 py-2.5 border-b border-slate-100"}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">
          <Clock className="h-3.5 w-3.5" /> Time clock
        </div>
        <span className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold ${meta.text}`} data-testid="time-clock-status">
          <span className={`h-2 w-2 rounded-full ${meta.dot} ${status === "clocked_in" ? "animate-pulse" : ""}`} />
          {meta.label}
        </span>
      </div>

      {status !== "clocked_out" && (
        <div className="mb-2.5 flex items-baseline gap-2">
          <span className="text-[22px] font-bold tabular-nums text-slate-900" data-testid="time-clock-session">
            {fmtDuration(live)}
          </span>
          <span className="text-[11px] text-slate-400">this session</span>
        </div>
      )}
      {state && status === "clocked_out" && (state.todaySeconds || 0) > 0 && (
        <div className="mb-2.5 text-[11.5px] text-slate-500" data-testid="time-clock-today">
          Today: <b className="text-slate-700">{fmtDuration(state.todaySeconds)}</b> worked
        </div>
      )}

      {loading ? (
        <div className="text-[12px] text-slate-400 flex items-center gap-1.5 py-1.5">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : (
        <div className={stacked ? "flex flex-col gap-2" : "flex items-center gap-1.5"}>
          {status === "clocked_out" && (
            <button
              onClick={clockIn}
              disabled={busy}
              data-testid="time-clock-in-btn"
              className={`${stacked ? "w-full" : "flex-1"} inline-flex items-center justify-center gap-1.5 h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[12.5px] font-semibold disabled:opacity-60 transition-colors`}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Clock In
            </button>
          )}
          {status === "clocked_in" && (
            <>
              <button
                onClick={lunchStart}
                disabled={busy}
                data-testid="time-clock-lunch-start-btn"
                className={`${stacked ? "w-full" : "flex-1"} inline-flex items-center justify-center gap-1.5 h-9 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 text-[12.5px] font-semibold disabled:opacity-60 transition-colors`}
              >
                <Coffee className="h-3.5 w-3.5" /> Lunch
              </button>
              <button
                onClick={clockOut}
                disabled={busy}
                data-testid="time-clock-out-btn"
                className={`${stacked ? "w-full" : "flex-1"} inline-flex items-center justify-center gap-1.5 h-9 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-[12.5px] font-semibold disabled:opacity-60 transition-colors`}
              >
                <Square className="h-3.5 w-3.5" /> Clock Out
              </button>
            </>
          )}
          {status === "on_lunch" && (
            <>
              <button
                onClick={lunchEnd}
                disabled={busy}
                data-testid="time-clock-lunch-end-btn"
                className={`${stacked ? "w-full" : "flex-1"} inline-flex items-center justify-center gap-1.5 h-9 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[12.5px] font-semibold disabled:opacity-60 transition-colors`}
              >
                <Utensils className="h-3.5 w-3.5" /> End Lunch
              </button>
              <button
                onClick={clockOut}
                disabled={busy}
                data-testid="time-clock-out-btn"
                className={`${stacked ? "w-full" : "flex-1"} inline-flex items-center justify-center gap-1.5 h-9 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-[12.5px] font-semibold disabled:opacity-60 transition-colors`}
              >
                <Square className="h-3.5 w-3.5" /> Clock Out
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default TimeClockPanel;
